import React, { useState, useRef } from "react";
import { useAuth } from "@/App";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import axios from "axios";
import { toast } from "sonner";
import {
  Microscope, Upload, Loader2, X, ShieldAlert, CheckCircle2,
  AlertCircle, Info, Eye, ImageIcon, Sparkles,
} from "lucide-react";
import CollapsiblePanel from "./CollapsiblePanel";
import { ComplianceDisclaimer, AIBadge, RiskIndicator } from "./compliance/ComplianceDisclaimer";

/**
 * EmbeddedOrPanel — когато `embedded=true`, рендерира само children без
 * CollapsiblePanel wrapper (за употреба в BreastHealthHub).
 */
function EmbeddedOrPanel({ embedded, children, ...panelProps }) {
  if (embedded) {
    return <div data-testid={`${panelProps.testId}-embedded`}>{children}</div>;
  }
  return <CollapsiblePanel {...panelProps}>{children}</CollapsiblePanel>;
}

const CONCERN_COLORS = {
  none: { bg: "bg-emerald-50", dark: "bg-emerald-900/20", text: "text-emerald-600", dot: "bg-emerald-500", label_bg: "Без притеснение", label_en: "Normal" },
  low: { bg: "bg-blue-50", dark: "bg-blue-900/20", text: "text-blue-600", dot: "bg-blue-500", label_bg: "Ниско", label_en: "Low" },
  moderate: { bg: "bg-amber-50", dark: "bg-amber-900/20", text: "text-amber-600", dot: "bg-amber-500", label_bg: "Умерено", label_en: "Moderate" },
  attention_needed: { bg: "bg-red-50", dark: "bg-red-900/20", text: "text-red-600", dot: "bg-red-500", label_bg: "Изисква внимание", label_en: "Attention needed" },
};

const SCAN_TYPES = [
  { id: "ultrasound", bg: "Ехография", en: "Ultrasound" },
  // iter707e: 'mammography' premахната от scan-type catalog — за плътност
  // вече се използва САМО ръчно въведеното BI-RADS от mammograph report-а
  // (ManualBiRadsCard в BHI). Mammography image upload не се поддържа.
  { id: "ct", bg: "CT скенер", en: "CT scan" },
  { id: "mri", bg: "ЯМР", en: "MRI" },
  { id: "xray", bg: "Рентген", en: "X-ray" },
  { id: "other", bg: "Друго", en: "Other" },
];

export default function MedicalImageScreening({ embedded = false, initialScanType = "ultrasound", lockScanType = false, onResult = null } = {}) {
  const { API, user, refreshUser } = useAuth();
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const bg = language === "bg";

  const [scanType, setScanType] = useState(initialScanType);
  const [context, setContext] = useState("");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [disclaimer, setDisclaimer] = useState(null);
  const fileInputRef = useRef(null);

  const isAdmin = user?.role === "admin";
  const credits = isAdmin ? 999 : (user?.credits ?? 0);
  const isBreastDensity = scanType === "mammography_density";
  const cost = isBreastDensity ? 2 : 5;
  const canAnalyze = (isAdmin || credits >= cost) && file;

  const handleFileSelect = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    // iter593 — приема images + DICOM (по-late content-type) (по име .dcm/.dicom)
    const isDicom = /\.(dcm|dicom)$/i.test(f.name) || /dicom/i.test(f.type || "");
    if (!f.type.startsWith("image/") && !isDicom) {
      toast.error(bg ? "Поддържат се само снимки (JPG/PNG) или DICOM (.dcm)" : "Only images (JPG/PNG) or DICOM (.dcm) supported");
      return;
    }
    if (f.size > 50 * 1024 * 1024) {
      toast.error(bg ? "Файлът е твърде голям (макс 50 MB)" : "File too large (max 50 MB)");
      return;
    }
    setFile(f);
    if (isDicom) {
      // DICOM — no inline preview; show filename only
      setPreview(null);
    } else {
      const reader = new FileReader();
      reader.onload = (ev) => setPreview(ev.target.result);
      reader.readAsDataURL(f);
    }
  };

  const removeFile = () => {
    setFile(null);
    setPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const analyze = async () => {
    if (!canAnalyze) return;
    setLoading(true);
    setResult(null);
    try {
      const form = new FormData();
      let data;
      if (isBreastDensity) {
        form.append("image", file);
        form.append("lang", bg ? "bg" : "en");
        const resp = await axios.post(`${API}/medical-image/breast-density`, form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        data = resp.data;
        const r = data.result;
        // iter593 — Когато engine_used === "unavailable" (production без torch/monai)
        // показваме явно съобщение вместо да крашваме на липсващи полета
        if (!r.available || r.engine_used === "unavailable") {
          setResult({
            overall_impression_level: "attention",
            overall_impression: bg
              ? "AI анализ временно недостъпен"
              : "AI analysis temporarily unavailable",
            observations: [
              {
                finding: bg ? "AI оценката не е активна" : "AI engine not active",
                concern_level: "info",
                note: bg ? r.user_action_bg : r.user_action_en,
              },
            ],
            reassuring_points: [],
            suggested_next_steps: [
              bg
                ? "Въведете BI-RADS категорията (A/B/C/D) ръчно от мамографския Ви доклад в Стъпка 1 по-горе"
                : "Enter the BI-RADS category (A/B/C/D) manually from your mammography report in Step 1 above",
            ],
            limitations: bg
              ? "MONAI Inception-v3 моделът не е активен в текущата среда. Това не пречи на изчисляването на Risk Profile — просто въведете BI-RADS ръчно."
              : "MONAI Inception-v3 model is not active in the current environment. You can still calculate the Risk Profile by entering BI-RADS manually.",
            birads_data: r,
          });
          setDisclaimer(bg ? data.disclaimer_bg : data.disclaimer_en);
          toast.info(bg ? "AI не е активен — въведете BI-RADS ръчно" : "AI not active — enter BI-RADS manually");
          return;
        }
        const confPct = Math.round((r.confidence || 0) * 100);
        const lowConfidence = (r.confidence || 0) < 0.55;
        // Adapt MONAI density result into generic result shape
        setResult({
          overall_impression_level: r.birads_category === "D" || r.birads_category === "C" ? "attention" : "normal_signs",
          overall_impression: bg
            ? `BI-RADS ${r.birads_category} — ${r.birads_label_bg} · Увереност ${confPct}%`
            : `BI-RADS ${r.birads_category} — ${r.birads_label_en} · Confidence ${confPct}%`,
          observations: [
            {
              finding: bg
                ? `BI-RADS категория: ${r.birads_category} (${r.birads_label_bg})`
                : `BI-RADS category: ${r.birads_category} (${r.birads_label_en})`,
              concern_level: lowConfidence ? "info" : (r.birads_category === "D" ? "attention" : "info"),
              note: bg
                ? `AI увереност ${confPct}%. Вероятности по класове: A ${Math.round((r.probabilities?.A||0)*100)}% · B ${Math.round((r.probabilities?.B||0)*100)}% · C ${Math.round((r.probabilities?.C||0)*100)}% · D ${Math.round((r.probabilities?.D||0)*100)}%`
                : `AI confidence ${confPct}%. Class probabilities: A ${Math.round((r.probabilities?.A||0)*100)}% · B ${Math.round((r.probabilities?.B||0)*100)}% · C ${Math.round((r.probabilities?.C||0)*100)}% · D ${Math.round((r.probabilities?.D||0)*100)}%`,
            },
          ],
          reassuring_points: (r.birads_category === "A" || r.birads_category === "B")
            ? [bg ? "По-ниска плътност → мамографията е по-чувствителна при тази категория" : "Lower density → mammography is more sensitive in this category"]
            : [],
          suggested_next_steps: [
            lowConfidence
              ? (bg ? "AI увереността е ниска — въведете BI-RADS ръчно от радиоложкия доклад за по-точна оценка" : "AI confidence is low — enter BI-RADS manually from the radiologist report for a more accurate result")
              : (r.birads_category === "D" || r.birads_category === "C"
                  ? (bg ? "Плътни гърди → обсъдете с радиолог допълнително изследване (ехография/ЯМР)" : "Dense breasts → discuss supplemental screening (ultrasound/MRI) with radiologist")
                  : (bg ? "Обичаен график на проверките" : "Standard screening schedule")),
            bg ? "Консултирайте се с лекар за интерпретация в контекста на целия Ви рисков профил" : "Consult a physician for interpretation within your full risk profile",
          ],
          limitations: bg
            ? `Модел: ${r.methodology || "MONAI Inception-v3 (Mayo Clinic Florida)"}. Файл: ${r.file_type?.toUpperCase()}. НЕ е одобрено медицинско устройство (CE/FDA). За диагностика — радиолог.`
            : `Model: ${r.methodology || "MONAI Inception-v3 (Mayo Clinic Florida)"}. File: ${r.file_type?.toUpperCase()}. NOT a medical device (CE/FDA). For diagnosis — radiologist.`,
          birads_data: r,
        });
        // Cross-reference hook: при Hub integration → signal за BI-RADS → Risk Profile
        onResult?.({ kind: "breast_density", birads: r.birads_category, percent: r.percent_density, confidence: r.confidence });
        setDisclaimer(bg ? data.disclaimer_bg : data.disclaimer_en);
      } else {
        form.append("file", file);
        form.append("scan_type", scanType);
        form.append("context", context || "");
        form.append("lang", bg ? "bg" : "en");
        const resp = await axios.post(`${API}/medical-image/analyze`, form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        data = resp.data;
        setResult(data.result);
        setDisclaimer(bg ? data.disclaimer_bg : data.disclaimer_en);
      }
      await refreshUser?.();
      toast.success(bg ? "Прегледът е готов" : "Review ready");
    } catch (e) {
      const detail = e.response?.data?.detail || "";
      if (e.response?.status === 402) {
        toast.error(bg ? "Достигнахте лимита си — надградете за повече анализи" : "You've reached your limit — upgrade for more analyses");
      } else {
        toast.error(detail || (bg ? "Грешка при анализ" : "Analysis error"));
      }
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setResult(null);
    setDisclaimer(null);
    setFile(null);
    setPreview(null);
    setContext("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  return (
    <EmbeddedOrPanel
      embedded={embedded}
      title={bg ? "Медицинско изображение" : "Medical Image Screening"}
      subtitle={bg ? "Ехография, CT/MRI, рентген" : "Ultrasound, CT/MRI, X-ray"}
      icon={Microscope}
      iconColor="#6366F1"
      credits={5}
      defaultOpen={false}
      storageKey="medical-image-screening"
      testId="medical-image-screening"
    >
      {!result ? (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <AIBadge />
            <RiskIndicator level="high" />
          </div>
          <ComplianceDisclaimer context="image" />

          {/* iter593 — Scan type selector скрит когато lockScanType=true (BHI density flow) */}
          {!lockScanType && (
            <div>
              <label className={`text-[10px] font-semibold mb-1 block ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                {bg ? "Тип изображение" : "Image type"}
              </label>
              <div className="grid grid-cols-3 gap-1.5" data-testid="scan-type-selector">
                {SCAN_TYPES.map(st => (
                  <button
                    key={st.id}
                    onClick={() => setScanType(st.id)}
                    className={`px-2 py-1.5 rounded-lg text-[10px] font-medium transition-all ${
                      scanType === st.id
                        ? "bg-gradient-to-r from-[#6366F1] to-[#8B5CF6] text-white shadow-sm"
                        : isDark ? "bg-[hsl(223,47%,12%)] text-gray-400 hover:bg-[hsl(223,47%,16%)]" : "bg-gray-100 text-gray-500 hover:bg-gray-200"
                    }`}
                    data-testid={`scan-type-${st.id}`}
                  >
                    {bg ? st.bg : st.en}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Upload — iter593: accept JPG/PNG + DICOM (.dcm) for breast density */}
          <input
            ref={fileInputRef}
            type="file"
            accept={isBreastDensity ? "image/*,.dcm,.dicom,application/dicom" : "image/*"}
            className="hidden"
            onChange={handleFileSelect}
            data-testid="medical-image-input"
          />
          {file ? (
            <div className="relative" data-testid="medical-image-preview">
              {preview ? (
                <img src={preview} alt="preview" className="w-full max-h-56 object-contain rounded-lg border" />
              ) : (
                // DICOM — no inline preview, show filename pill
                <div className={`flex items-center gap-2 p-3 rounded-lg border ${
                  isDark ? "bg-[hsl(223,47%,10%)] border-indigo-500/30" : "bg-indigo-50 border-indigo-200"
                }`}>
                  <Microscope className={`w-5 h-5 shrink-0 ${isDark ? "text-indigo-300" : "text-indigo-700"}`} />
                  <div className="min-w-0 flex-1">
                    <p className={`text-[12px] font-semibold truncate ${isDark ? "text-indigo-100" : "text-indigo-900"}`}>{file.name}</p>
                    <p className={`text-[10px] ${isDark ? "text-indigo-300/70" : "text-indigo-700/70"}`}>
                      DICOM · {(file.size / 1024 / 1024).toFixed(1)} MB
                    </p>
                  </div>
                </div>
              )}
              <button
                onClick={removeFile}
                className={`absolute top-1 right-1 p-1 rounded-full ${isDark ? "bg-gray-800/80 text-gray-300" : "bg-white/90 text-gray-600"}`}
                data-testid="medical-image-remove"
              >
                <X className="w-3.5 h-3.5" />
              </button>
              {preview && (
                <div className={`absolute bottom-1 left-1 px-1.5 py-0.5 rounded text-[10px] ${isDark ? "bg-gray-800/80 text-gray-300" : "bg-white/90 text-gray-600"}`}>
                  {(file.size / 1024 / 1024).toFixed(1)} MB
                </div>
              )}
            </div>
          ) : (
            <Button
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              className={`w-full h-20 border-dashed ${isDark ? "bg-[hsl(223,47%,10%)] border-[hsl(223,47%,25%)] hover:bg-[hsl(223,47%,12%)]" : ""}`}
              data-testid="medical-image-upload-btn"
            >
              <div className="flex flex-col items-center gap-1">
                <Upload className="w-5 h-5 text-[#6366F1]" />
                <span className="text-xs">
                  {bg ? "Качете снимка (JPG/PNG, до 15 MB)" : "Upload image (JPG/PNG, up to 15 MB)"}
                </span>
              </div>
            </Button>
          )}

          {/* Context (optional) */}
          <Textarea
            value={context}
            onChange={(e) => setContext(e.target.value)}
            placeholder={bg ? "Симптоми или контекст (незадължително)" : "Symptoms or context (optional)"}
            rows={2}
            maxLength={500}
            className={`text-xs ${isDark ? "bg-[hsl(223,47%,10%)] border-[hsl(223,47%,20%)] text-gray-200" : ""}`}
            data-testid="medical-image-context"
          />

          <Button
            onClick={analyze}
            disabled={!canAnalyze || loading}
            className="w-full h-9 bg-gradient-to-r from-[#6366F1] to-[#8B5CF6] hover:opacity-90 text-white text-xs font-semibold disabled:opacity-50"
            data-testid="medical-image-analyze-btn"
          >
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Sparkles className="w-3.5 h-3.5 mr-1.5" />}
            {loading ? (bg ? "Анализ..." : "Analyzing...") : (bg ? "Анализирай" : "Analyze")}
          </Button>

          {credits < 5 && (
            <p className="text-[11px] text-amber-500">
              {bg ? "Достигнахте лимита на пробния / плана. Надградете за повече анализи." : "You've reached your trial / plan limit. Upgrade for more analyses."}
            </p>
          )}
        </div>
      ) : (
        <div className="space-y-3" data-testid="medical-image-result">
          {/* Disclaimer permanent */}
          {disclaimer && (
            <div className={`p-2.5 rounded-lg text-[11px] flex items-start gap-2 ${isDark ? "bg-amber-900/20 text-amber-200" : "bg-amber-50 text-amber-900"}`}>
              <ShieldAlert className="w-3.5 h-3.5 flex-shrink-0 mt-0.5 text-amber-500" />
              <span>{disclaimer}</span>
            </div>
          )}

          {/* Quality + anatomy */}
          <div className={`p-2.5 rounded-lg ${isDark ? "bg-[hsl(223,47%,10%)]" : "bg-gray-50"}`}>
            {result.quality_assessment && (
              <p className={`text-[11px] ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                <Eye className="w-3.5 h-3.5 inline mr-1 text-[#6366F1]" />
                {bg ? "Качество: " : "Quality: "}<span className="font-medium">{result.quality_assessment}</span>
              </p>
            )}
            {result.anatomy_visible?.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {result.anatomy_visible.slice(0, 8).map((a, i) => (
                  <span key={`anat-${i}-${String(a).slice(0, 10)}`} className={`text-[10px] px-1.5 py-0.5 rounded ${isDark ? "bg-[hsl(223,47%,14%)] text-gray-300" : "bg-white text-gray-600 border"}`}>
                    {a}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Observations */}
          {result.observations?.length > 0 && (
            <div data-testid="medical-observations">
              <h4 className={`text-xs font-bold mb-1.5 ${isDark ? "text-white" : "text-gray-800"}`}>
                {bg ? "Наблюдения" : "Observations"}
              </h4>
              <div className="space-y-2">
                {result.observations.map((obs, i) => {
                  const cc = CONCERN_COLORS[obs.concern_level] || CONCERN_COLORS.none;
                  return (
                    <div key={`obs-${i}-${String(obs.finding || '').slice(0, 15)}`} className={`p-2.5 rounded-lg ${isDark ? cc.dark : cc.bg}`}>
                      <div className="flex items-start gap-2">
                        <span className={`w-2 h-2 rounded-full ${cc.dot} flex-shrink-0 mt-1`}></span>
                        <div className="flex-1 min-w-0">
                          <p className={`text-xs font-semibold ${isDark ? "text-white" : "text-gray-800"}`}>
                            {obs.finding}
                            <span className={`ml-1.5 text-[9px] font-medium ${cc.text}`}>
                              [{bg ? cc.label_bg : cc.label_en}]
                            </span>
                          </p>
                          {obs.location && (
                            <p className={`text-[10px] italic mt-0.5 ${isDark ? "text-gray-500" : "text-gray-500"}`}>
                              📍 {obs.location}
                            </p>
                          )}
                          {obs.notes && (
                            <p className={`text-[11px] mt-1 ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                              {obs.notes}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Reassuring */}
          {result.reassuring_points?.length > 0 && (
            <div data-testid="medical-reassuring">
              <h4 className={`text-xs font-bold mb-1.5 flex items-center gap-1.5 ${isDark ? "text-emerald-400" : "text-emerald-600"}`}>
                <CheckCircle2 className="w-3.5 h-3.5" />
                {bg ? "Нормални находки" : "Reassuring findings"}
              </h4>
              <ul className="space-y-0.5 pl-1">
                {result.reassuring_points.slice(0, 5).map((p, i) => (
                  <li key={`re-${i}-${String(p).slice(0, 15)}`} className={`text-[11px] pl-4 relative ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                    <span className="absolute left-0 text-emerald-500">✓</span> {p}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Next steps */}
          {result.suggested_next_steps?.length > 0 && (
            <div className={`p-2.5 rounded-lg ${isDark ? "bg-[#6366F1]/10" : "bg-indigo-50"}`} data-testid="medical-next-steps">
              <h4 className={`text-xs font-bold mb-1.5 flex items-center gap-1.5 ${isDark ? "text-indigo-400" : "text-indigo-600"}`}>
                <Info className="w-3.5 h-3.5" />
                {bg ? "Препоръчителни следващи стъпки" : "Recommended next steps"}
              </h4>
              <ul className="space-y-0.5 pl-4">
                {result.suggested_next_steps.map((s, i) => (
                  <li key={`ns-${i}-${String(s).slice(0, 15)}`} className={`text-[11px] list-disc ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                    {s}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Limitations */}
          {result.limitations && (
            <p className={`text-[10px] italic ${isDark ? "text-gray-500" : "text-gray-500"}`}>
              {bg ? "Ограничения: " : "Limitations: "}{result.limitations}
            </p>
          )}

          <Button variant="outline" size="sm" onClick={reset} className="w-full h-8 text-xs" data-testid="medical-image-new-btn">
            {bg ? "Нов анализ" : "New analysis"}
          </Button>
        </div>
      )}
    </EmbeddedOrPanel>
  );
}
